'use client';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '@benzinga/core-ui';
import { truncate } from '@benzinga/utils';

export const ABOUT_DESCRIPTION_LIMIT = 250;

export type QuoteAboutMetaItem = {
  href?: string;
  label: string;
  value: string;
};

export const formatWebsiteHost = (url: string): string => {
  try {
    const normalized = url.startsWith('http') ? url : `https://${url}`;
    return new URL(normalized).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export const QuoteAboutCard = ({
  companyName,
  description,
  metaItems,
}: {
  companyName: string;
  description?: string;
  metaItems: QuoteAboutMetaItem[];
}): React.ReactElement | null => {
  const { t } = useTranslation(['quote', 'common']);
  const [shortened, setShortened] = useState(true);
  const aboutDescription = description?.trim() ?? '';
  const heading = `${t('Quote.About.about')}${companyName ? ` ${companyName}` : ''}`;

  if (!companyName && !aboutDescription && metaItems.length === 0) return null;

  return (
    <div className="card flex w-full flex-col gap-3 p-4">
      <SectionTitle capitalize={false} className="!mb-0" level={2} size="xl" titleText={heading} uppercase={false}>
        {heading}
      </SectionTitle>
      {metaItems.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {metaItems.map(item => (
            <div className="flex min-w-0 flex-col gap-0.5" key={item.label}>
              <div className="text-xs text-gray-500">{item.label}</div>
              {item.href ? (
                <a
                  className="truncate text-sm font-bold text-blue-600 hover:underline"
                  href={item.href}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {item.value}
                </a>
              ) : (
                <div className="text-sm font-bold text-bzgray-900">{item.value}</div>
              )}
            </div>
          ))}
        </div>
      ) : null}
      {aboutDescription ? (
        <p className="text-sm text-grey-darker">
          {shortened ? truncate(aboutDescription, ABOUT_DESCRIPTION_LIMIT, ' ') : aboutDescription}
        </p>
      ) : null}
      {aboutDescription.length > ABOUT_DESCRIPTION_LIMIT ? (
        <button
          className="self-start text-sm font-bold uppercase text-blue-600 hover:underline"
          onClick={() => setShortened(current => !current)}
          type="button"
        >
          {shortened ? t('Buttons.show-more', { ns: 'common' }) : t('Buttons.show-less', { ns: 'common' })}
        </button>
      ) : null}
    </div>
  );
};
